import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  unsubscribeFromCollaboratorChanges,
  preloadCollaborators,
} from '../components/projects/services/projectCollaboratorService';
import { logger } from '../utils/logger';
import { supabase } from '../lib/supabase';

/**
 * Interface for the Project UI Store state
 * This store handles UI-specific state that doesn't need to be persisted via React Query
 */
interface ProjectUIState {
  // Active project tracking
  activeProjectId: string | null;
  setActiveProject: (projectId: string | null) => void;

  // UI state flags
  isCreatingProject: boolean;
  setIsCreatingProject: (isCreating: boolean) => void;

  // Archive visibility state
  showArchived: boolean;
  setShowArchived: (show: boolean) => void;

  // For updating last selected (without using hooks in the store)
  updateActiveProjectTimestamp: () => Promise<void>;
}

/**
 * Store for managing project UI state
 * Separated from data state which is managed by React Query
 */
export const useProjectUIStore = create<ProjectUIState>()(
  persist(
    (set, get) => ({
      // Active project tracking
      activeProjectId: null,

      // UI state flags
      isCreatingProject: false,

      // Archive visibility state
      showArchived: false,

      /**
       * Set the active project and handle side effects
       */
      setActiveProject: (projectId: string | null) => {
        // Get the previous project ID to unsubscribe from
        const previousProjectId = get().activeProjectId;

        // Set the new active project ID
        set({ activeProjectId: projectId });

        // Clean up the previous project's subscriptions if it exists
        if (previousProjectId && previousProjectId !== projectId) {
          logger.realtime('Unsubscribing from previous project collaborator changes', {
            previousProjectId,
          });
          unsubscribeFromCollaboratorChanges(previousProjectId);
        }

        if (projectId) {
          // Update selection timestamp directly - no hooks in store!
          get().updateActiveProjectTimestamp();

          // Preload collaborator data for this project to populate user cache
          // This ensures assignee info is available throughout the app
          preloadCollaborators(projectId).catch(error => {
            // Log but don't disrupt the UI flow
            logger.error('Failed to preload collaborators', {
              projectId,
              error: error instanceof Error ? error.message : String(error),
            });
          });
        }
      },

      /**
       * Update timestamp directly from Supabase without using React Query hooks
       * This avoids using hooks inside the store which is an anti-pattern
       */
      updateActiveProjectTimestamp: async () => {
        const projectId = get().activeProjectId;
        if (!projectId) return;

        const now = new Date().toISOString();
        try {
          await supabase
            .from('projects')
            .update({
              last_selected_at: now,
              updated_at: now,
            })
            .eq('id', projectId);

          logger.store('Updated project last_selected_at timestamp', { projectId });
        } catch (error) {
          logger.error('❌ Error updating last_selected_at:', error);
        }
      },

      /**
       * Toggle project creation modal
       */
      setIsCreatingProject: (isCreating: boolean) => {
        set({ isCreatingProject: isCreating });
      },

      /**
       * Set visibility of archived projects
       */
      setShowArchived: (show: boolean) => {
        set({ showArchived: show });
      },
    }),
    {
      name: 'project-ui-storage',
      storage: createJSONStorage(() => {
        // Use a try-catch in case localStorage is unavailable
        try {
          return localStorage;
        } catch (error) {
          // Fall back to in-memory storage
          logger.warn('LocalStorage unavailable, using in-memory storage', { error });
          const memoryStorage: Record<string, string> = {};
          return {
            getItem: (name: string) => memoryStorage[name] || null,
            setItem: (name: string, value: string) => {
              memoryStorage[name] = value;
            },
            removeItem: (name: string) => {
              delete memoryStorage[name];
            },
          };
        }
      }),
      // Only persist these keys
      partialize: state => ({
        activeProjectId: state.activeProjectId,
        showArchived: state.showArchived,
      }),
    }
  )
);
